import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchAccountMappings } from "../api/fetchAccountMappings/fetchAccountMappingsApi";
import {
  AccountMappingRow,
  LedgerAccountType,
} from "../api/fetchAccountMappings/types";
import { fetchLedgerAccounts } from "../api/fetchLedgerAccounts/fetchLedgerAccountsApi";
import { LedgerAccount } from "../api/fetchLedgerAccounts/types";
import { saveAccountMappings } from "../api/saveAccountMappings/saveAccountMappingsApi";
import { SaveAccountMappingRow } from "../api/saveAccountMappings/types";
import { autoMapAccounts } from "../api/autoMapAccounts/autoMapAccountsApi";
import {
  DO_NOT_MAP_CODE,
  FilterTab,
  MATERIAL_BALANCE_THRESHOLD,
} from "../config/accountMappingDisplay";

export interface LedgerAccountGroup {
  label: string;
  ledgerAccountType: LedgerAccountType;
  isRecommended: boolean;
  categories: LedgerAccount[];
}

export interface PendingExclusion {
  qboAccountId: string;
  name: string;
  currentBalance: number | null;
}

export const useAccountMappingSection = (companyUuid?: string) => {
  const [accounts, setAccounts] = useState<AccountMappingRow[]>([]);
  const [categories, setCategories] = useState<LedgerAccount[]>([]);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isAutoMapping, setIsAutoMapping] = useState(false);
  const [activeTab, setActiveTab] = useState<FilterTab>("review");
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingExclusion, setPendingExclusion] =
    useState<PendingExclusion | null>(null);
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<string>>(
    new Set(),
  );
  const [savingAccountIds, setSavingAccountIds] = useState<Set<string>>(
    new Set(),
  );

  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelPendingRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const createSignal = useCallback(() => {
    cancelPendingRequest();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller.signal;
  }, [cancelPendingRequest]);

  /**
   * `isBackground` keeps the table on screen while it refreshes. Only the first
   * load has nothing to show and earns the full-page spinner; a refresh after a
   * save would otherwise unmount the whole section and throw away scroll
   * position for a round trip the member did not ask to watch.
   */
  const loadMappings = useCallback(
    (isBackground = false) => {
      if (!companyUuid) {
        return Promise.resolve();
      }

      const signal = createSignal();

      if (!isBackground) {
        setIsLoading(true);
      }

      return fetchAccountMappings(companyUuid, signal)
        .then((response) => {
          setAccounts(response.data.accounts);
          setSyncedAt(response.data.syncedAt);
          // Rows that were just confirmed are no longer selectable, and stale ids
          // would silently re-confirm on the next batch.
          setSelectedAccountIds(new Set());
        })
        .finally(() => {
          if (!isBackground) {
            setIsLoading(false);
          }
        });
    },
    [companyUuid, createSignal],
  );

  useEffect(() => {
    fetchLedgerAccounts().then((response) => {
      setCategories(response.data);
    });
  }, []);

  useEffect(() => {
    loadMappings();
    return cancelPendingRequest;
  }, [loadMappings, cancelPendingRequest]);

  const persist = useCallback(
    (rows: SaveAccountMappingRow[]) => {
      if (!companyUuid || rows.length === 0) {
        return;
      }

      const savingIds = rows.map((row) => row.qboAccountId);

      setIsSaving(true);
      // Added rather than replaced: two rows changed in quick succession are
      // both in flight, and the first to land must not clear the second.
      setSavingAccountIds((current) => new Set([...current, ...savingIds]));

      saveAccountMappings(companyUuid, { mappings: rows }).finally(() => {
        // Refresh after a failed save too — an optimistic row that never
        // persisted has to snap back to what the server actually holds.
        loadMappings(true).finally(() => {
          setIsSaving(false);
          setSavingAccountIds((current) => {
            const next = new Set(current);
            savingIds.forEach((id) => next.delete(id));
            return next;
          });
        });
      });
    },
    [companyUuid, loadMappings],
  );

  /**
   * Shows the member's choice the moment they make it, so the select does not
   * snap back to the old value for the length of the save. Only what the row
   * can derive locally is set — `status`, `flags` and `confidence` are the
   * server's to decide and arrive with the refresh rather than being guessed at
   * here.
   */
  const applyLedgerAccountLocally = useCallback(
    (qboAccountId: string, ledgerAccountCode: string | null) => {
      setAccounts((current) =>
        current.map((account) => {
          if (account.qboAccountId !== qboAccountId) {
            return account;
          }

          if (ledgerAccountCode === null) {
            return { ...account, mapping: null };
          }

          const ledgerAccount = categories.find(
            (candidate) => candidate.code === ledgerAccountCode,
          );

          return {
            ...account,
            mapping: {
              ledgerAccountCode,
              ledgerAccountLabel: ledgerAccount?.label ?? "",
              suggestedLedgerAccountCode:
                account.mapping?.suggestedLedgerAccountCode ?? null,
              source: "manual",
              confidence: null,
              isConfirmed: false,
              isCrossType:
                ledgerAccount !== undefined &&
                ledgerAccount.ledgerAccountType !== account.ledgerAccountType,
            },
          };
        }),
      );
    },
    [categories],
  );

  const changeLedgerAccount = useCallback(
    (qboAccountId: string, ledgerAccountCode: string | null) => {
      const account = accounts.find(
        (candidate) => candidate.qboAccountId === qboAccountId,
      );

      const balance = account?.currentBalance ?? null;
      const isMaterial =
        balance !== null && Math.abs(balance) >= MATERIAL_BALANCE_THRESHOLD;

      if (ledgerAccountCode === DO_NOT_MAP_CODE && account && isMaterial) {
        setPendingExclusion({
          qboAccountId,
          name: account.name,
          currentBalance: account.currentBalance,
        });
        return;
      }

      applyLedgerAccountLocally(qboAccountId, ledgerAccountCode);
      persist([{ qboAccountId, ledgerAccountCode, isConfirmed: false }]);
    },
    [accounts, persist, applyLedgerAccountLocally],
  );

  const confirmExclusion = useCallback(() => {
    if (!pendingExclusion) {
      return;
    }

    persist([
      {
        qboAccountId: pendingExclusion.qboAccountId,
        ledgerAccountCode: DO_NOT_MAP_CODE,
        isConfirmed: true,
      },
    ]);
    setPendingExclusion(null);
  }, [pendingExclusion, persist]);

  const cancelExclusion = useCallback(() => {
    setPendingExclusion(null);
  }, []);

  const toggleAccountSelection = useCallback((qboAccountId: string) => {
    setSelectedAccountIds((current) => {
      const next = new Set(current);

      if (next.has(qboAccountId)) {
        next.delete(qboAccountId);
      } else {
        next.add(qboAccountId);
      }

      return next;
    });
  }, []);

  const confirmSelected = useCallback(() => {
    persist(
      accounts
        .filter(
          (account) =>
            selectedAccountIds.has(account.qboAccountId) && account.mapping,
        )
        .map((account) => ({
          qboAccountId: account.qboAccountId,
          ledgerAccountCode: account.mapping?.ledgerAccountCode ?? null,
          isConfirmed: true,
        })),
    );
  }, [accounts, selectedAccountIds, persist]);

  const runAutoMap = useCallback(() => {
    if (!companyUuid) {
      return;
    }

    setIsAutoMapping(true);

    autoMapAccounts(companyUuid).finally(() => {
      loadMappings(true).finally(() => {
        setIsAutoMapping(false);
      });
    });
  }, [companyUuid, loadMappings]);

  const counts = useMemo(() => {
    const tally = {
      review: 0,
      ai_mapped: 0,
      resolved: 0,
      all: accounts.length,
    };

    accounts.forEach((account) => {
      if (account.status === "unmapped" || account.status === "review") {
        tally.review += 1;
      } else if (account.status === "ai_mapped") {
        tally.ai_mapped += 1;
      } else {
        tally.resolved += 1;
      }
    });

    return tally;
  }, [accounts]);

  const visibleAccounts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return accounts.filter((account) => {
      const matchesTab =
        activeTab === "all" ||
        (activeTab === "review" &&
          (account.status === "unmapped" || account.status === "review")) ||
        (activeTab === "ai_mapped" && account.status === "ai_mapped") ||
        (activeTab === "resolved" &&
          (account.status === "confirmed" || account.status === "overridden"));

      const matchesQuery =
        query === "" ||
        account.name.toLowerCase().includes(query) ||
        (account.accountNumber ?? "").toLowerCase().includes(query) ||
        (account.accountSubType ?? "").toLowerCase().includes(query);

      return matchesTab && matchesQuery;
    });
  }, [accounts, activeTab, searchQuery]);

  const buildLedgerAccountGroups = useCallback(
    (accountLedgerAccountType: LedgerAccountType): LedgerAccountGroup[] => {
      const byBucket = new Map<LedgerAccountType, LedgerAccount[]>();

      categories.forEach((category) => {
        const bucket = byBucket.get(category.ledgerAccountType) ?? [];
        bucket.push(category);
        byBucket.set(category.ledgerAccountType, bucket);
      });

      const recommended = byBucket.get(accountLedgerAccountType) ?? [];
      const groups: LedgerAccountGroup[] = [];

      if (recommended.length > 0) {
        groups.push({
          label: "Recommended for this account",
          ledgerAccountType: accountLedgerAccountType,
          isRecommended: true,
          categories: recommended,
        });
      }

      // The API returns the catalog ordered by type heading, then by label, and
      // a Map keeps insertion order — so grouping alone preserves both levels
      // and nothing here re-sorts.
      byBucket.forEach((bucketCategories, bucket) => {
        if (bucket === accountLedgerAccountType) {
          return;
        }

        groups.push({
          label: bucketCategories[0].ledgerAccountTypeLabel,
          ledgerAccountType: bucket,
          isRecommended: false,
          categories: bucketCategories,
        });
      });

      return groups;
    },
    [categories],
  );

  const unmappedCount = useMemo(
    () => accounts.filter((account) => account.status === "unmapped").length,
    [accounts],
  );

  /** A row can be approved once it has a category and has not been confirmed. */
  const isSelectable = useCallback(
    (account: AccountMappingRow): boolean =>
      account.mapping !== null && !account.mapping.isConfirmed,
    [],
  );

  const selectableVisibleIds = useMemo(
    () =>
      visibleAccounts
        .filter(isSelectable)
        .map((account) => account.qboAccountId),
    [visibleAccounts, isSelectable],
  );

  const areAllVisibleSelected =
    selectableVisibleIds.length > 0 &&
    selectableVisibleIds.every((id) => selectedAccountIds.has(id));

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedAccountIds((current) => {
      const next = new Set(current);
      const allSelected = selectableVisibleIds.every((id) => next.has(id));

      selectableVisibleIds.forEach((id) =>
        allSelected ? next.delete(id) : next.add(id),
      );

      return next;
    });
  }, [selectableVisibleIds]);

  return {
    accounts,
    visibleAccounts,
    categories,
    counts,
    syncedAt,
    isLoading,
    isSaving,
    savingAccountIds,
    isAutoMapping,
    activeTab,
    setActiveTab,
    searchQuery,
    setSearchQuery,
    unmappedCount,
    pendingExclusion,
    confirmExclusion,
    cancelExclusion,
    changeLedgerAccount,
    selectedAccountIds,
    selectedCount: selectedAccountIds.size,
    isSelectable,
    toggleAccountSelection,
    toggleSelectAllVisible,
    areAllVisibleSelected,
    hasSelectableVisibleRows: selectableVisibleIds.length > 0,
    confirmSelected,
    runAutoMap,
    refresh: loadMappings,
    buildLedgerAccountGroups,
  };
};
